/**
 * @file        src/pages/insurance/InvoiceDoc.jsx
 * @description Admin-native tax invoice for a STANDALONE travel-insurance
 *              policy (`insurance_bookings`), at
 *              /insurance/bookings/:id/invoice.
 *
 *              A faithful port of the insurance invoice this console ALREADY
 *              prints for the flight add-on (flight/InvoiceDoc's
 *              FlightInsuranceInvoiceDoc) and of the customer's own copy (each
 *              storefront's pages/Invoice). Same sheet, same From/To block,
 *              same 5-column line table under SAC 998559, same GST Details +
 *              totals split, same Terms — so one product does not have two
 *              differently-shaped invoices depending on who printed it.
 *
 *              THREE deliberate differences from the flight add-on version,
 *              all because this policy was bought on its own:
 *                • it cites no "Against Flight Invoice No." — there is no
 *                  parent booking
 *                • its number is the standalone 'insurance' invoice_type from
 *                  the configured series, not 'flight_insurance'
 *                • the paid-note names this booking, not a flight
 *
 *              Portrait, unlike the flight ticket invoice: this document is 5
 *              columns and fits an A4 portrait sheet, so it deliberately does
 *              NOT inject the landscape @page rule.
 *
 *              The status rule is the shared one (lib/docAccess): an invoice
 *              exists for a confirmed or cancelled/refunded policy and for
 *              nothing else. Gated HERE as well as on the row menu, so a pasted
 *              URL cannot print an invoice for a policy that was never paid for.
 * @author      Kinetika Labs
 * @created     2026-09-22
 * @changelog
 *   - 2026-09-25 Claude — The per-traveller lines and the GST block finally have
 *     data behind them: the API served `supplier_ref` as `details`, which holds
 *     {plid,pid,perTraveller} and never the insured travellers or the insurer's
 *     tax split, so this document printed ONE line naming the account holder and
 *     hid GST entirely on every policy. Fixed in insurance.repo/insuranceReads;
 *     this file changed only where it was itself wrong — the "To" block now
 *     prints the CHECKOUT contact (it printed the account profile's mobile,
 *     disagreeing with the customer's copy) and the CGST/SGST cells go through
 *     lib/insuranceFare, which falls back to the even 9/9 split when the insurer
 *     states a GST total with no heads and stops hardcoding IGST to 0.
 *   - 2026-09-23 Claude — "Back to Bookings" returns to the list this document
 *                          was opened from. This route is shared by the B2C and
 *                          the Corporate screen, so Back always landed on the
 *                          B2C list; lib/docOrigin carries which it was.
 *   - 2026-09-22 Claude — The Terms list appends the tenant's configured invoice
 *     clauses (Payment Management → Tax Management), which the flight and hotel
 *     invoices have printed since 2026-09-15 and this one never did.
 *   - 2026-09-22 Claude — Initial (B2C Insurance + Corporate Insurance).
 */
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { backToList } from '../../lib/docOrigin';
import { useQuery } from '@tanstack/react-query';
import Icon from '../../components/Icon';
import DocBrandMark from '../../components/DocBrandMark';
import { get } from '../../api/client';
import { useCompany } from '../../lib/useCompany';
import { isSet, extraTerms } from '../../lib/company';
import { insuranceGstSplit } from '../../lib/insuranceFare';
import { docAccess } from './cells';

const n2 = (v) => (Number(v) || 0).toFixed(2);
const rupee = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const r2m = (v) => Math.round((Number(v) || 0) * 100) / 100;

const fmtDate = (v) => {
  const d = v ? new Date(v) : new Date();
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
/** Unlike fmtDate, a MISSING cover date is '—', never today. */
const coverDate = (v) => (v ? fmtDate(v) : '—');

// SAC for "other insurance services" — what a premium is billed under. The same
// constant the flight module's insurance invoice uses.
const INSURANCE_SAC = '998559';

/**
 * Split an authoritative total across rows in proportion to per-row weights.
 * Ported verbatim from flight/InvoiceDoc so the per-traveller amounts on the two
 * insurance invoices are computed identically. The remainder lands on the last
 * row carrying weight, so the column sums to EXACTLY the stated total — on an
 * invoice a lost paisa is a real defect, not rounding.
 */
const splitByWeight = (total, weights) => {
  const c = Math.max(1, weights.length);
  const t = Number(total) || 0;
  const w = weights.map((x) => Math.max(0, Number(x) || 0));
  const sum = w.reduce((s, x) => s + x, 0);
  if (!t) return Array.from({ length: c }, () => 0);
  if (sum <= 0) {
    const a = Array.from({ length: c }, () => 0);
    a[0] = r2m(t);
    return a;
  }
  const arr = w.map((x) => r2m((t * x) / sum));
  const last = w.reduce((idx, x, i) => (x > 0 ? i : idx), 0);
  arr[last] = r2m(t - arr.reduce((s, v, i) => (i === last ? s : s + v), 0));
  return arr;
};

/** From (the configured issuer) / To (the buyer's contact). Same block, same order. */
function PartiesBlock({ b, company }) {
  return (
    <div className="doc-parties">
      <div>
        <p className="doc-h">From</p>
        <p><b>{company.legal}</b></p>
        <p><b>Address</b>: {company.address}</p>
        <p><b>Email</b>: {company.email}</p>
        <p><b>Phone</b>: {company.phone}</p>
        {isSet(company.gstin) ? <p><b>GSTIN</b>: {company.gstin}</p> : null}
        {isSet(company.pan) ? <p><b>PAN</b>: {company.pan}</p> : null}
        {isSet(company.registrationNo) ? <p><b>Reg. No</b>: {company.registrationNo}</p> : null}
        {isSet(company.gstStateCode) ? <p><b>GST State Code</b>: {company.gstStateCode}</p> : null}
        {isSet(company.hsn) ? <p><b>HSN/SAC</b>: {company.hsn}</p> : null}
      </div>
      <div className="doc-to">
        <p className="doc-h">To</p>
        {/* The contact given AT CHECKOUT, which is who this document is
            addressed to — not the account profile. The buyer's own details are
            the drawer's Customer panel. This copy printed the profile's mobile
            while the customer's copy printed the booking contact, so one
            invoice carried two different phone numbers. */}
        <p><b>Email</b>: {b.contact_email || b.customer_email || '—'}</p>
        <p><b>Phone</b>: {b.contact_phone || b.customer_phone || '—'}</p>
      </div>
    </div>
  );
}

export default function InsuranceInvoiceDoc({ bookingIdOverride } = {}) {
  const { id: routeId } = useParams();
  const id = bookingIdOverride ?? routeId;
  const navigate = useNavigate();
  // Which list to return to — this document route is shared by the B2C and the
  // Corporate screen (lib/docOrigin).
  const { search } = useLocation();
  const COMPANY = useCompany();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['insurance-booking-details', Number(id)],
    queryFn: () => get(`/insurance/bookings/${id}`),
    enabled: !!id,
  });
  const b = data?.data;

  useEffect(() => {
    if (b) document.title = `Insurance Invoice ${b.booking_reference} · ${COMPANY.name}`;
  }, [b, COMPANY.name]);

  if (isLoading) return <div className="panel glass"><div className="empty">Preparing invoice…</div></div>;
  if (isError || !b) return <div className="panel glass"><div className="empty">Could not load this policy.</div></div>;

  // Same gate as the row menu — a pasted URL must not print an invoice for a
  // policy that was never paid for.
  if (!docAccess(b.booking_status).invoice) {
    return (
      <div className="panel glass">
        <div className="empty">
          No invoice exists for a {String(b.booking_status || 'pending').toLowerCase()} policy.
        </div>
      </div>
    );
  }

  const d = b.details || {};
  const total = r2m(b.total_paid);

  // The insurer's OWN split: SP is the net premium, SPGST the tax on it, and
  // CGST/SGST are that tax's halves — not extra charges. Absent when no policy
  // was issued; only the charged total is stated then, never a guessed
  // breakdown on a tax document.
  const fare = d.fare || {};
  const { premium, gst: premiumGst, cgst, sgst, igst, hasSplit: hasFareSplit } = insuranceGstSplit(fare);
  // Our margin over the supplier's premium + its GST, so the lines reconcile to
  // exactly the charged total instead of leaving an unexplained residual.
  // Prefer the stored `markup` column (authoritative since 2026-08-10) and fall
  // back to the computed gap for a policy sold before it existed.
  const storedMarkup = r2m(b.markup);
  const processing = hasFareSplit
    ? (storedMarkup > 0 ? storedMarkup : Math.max(0, r2m(total - premium - premiumGst)))
    : 0;

  // One line per insured traveller, the charged total apportioned by each
  // traveller's own supplier premium so the column sums to exactly `total`
  // even though our margin is booking-level.
  const travellers = Array.isArray(d.travellers) ? d.travellers : [];
  const amountSplit = splitByWeight(total, travellers.map((t) => t.premium));
  const lines = travellers.length
    ? travellers.map((t, i) => ({ name: t.name, policyId: t.policyId, amount: amountSplit[i] ?? 0 }))
    : [{ name: b.customer_name || null, policyId: b.policy_id || null, amount: total }];

  const planFacts = [
    ['Insurer', b.insurer],
    ['Plan', b.plan_tier],
    ['Cover', b.plan_cover],
    ['Destination', b.destination],
    ['Cover Period', (b.start_date || b.end_date) ? `${coverDate(b.start_date)} – ${coverDate(b.end_date)}` : null],
  ].filter(([, v]) => v);

  const invoiceDate = fmtDate(b.created_at);

  return (
    <>
      <div className="doc-bar">
        <button className="btn ghost" onClick={() => navigate(backToList(search, '/insurance/bookings', '/insurance/corporate-bookings'))}>
          <Icon name="chev" style={{ transform: 'rotate(180deg)' }} /> Back to Insurance List
        </button>
        <div className="doc-bar-actions">
          <button className="btn primary" onClick={() => window.print()}>
            <Icon name="download" /> Print / Save as PDF
          </button>
        </div>
      </div>

      <article className="doc-sheet">
        <DocBrandMark brandCode={b.brand} className="doc-brand-logo" />
        <h1 className="doc-title">INSURANCE INVOICE</h1>

        <div className="doc-meta">
          <p>No.: {b.booking_reference || '—'}</p>
          <p>Invoice Date: {invoiceDate}</p>
        </div>
        {/* The REAL issued number from the configured series. A policy placed
            before invoicing was wired into the payment callbacks has none, and
            this states that rather than deriving a number in the browser — the
            defect that made the admin and customer copies disagree. */}
        <p className="doc-invno">
          Invoice No.: {b.invoice_number || <span className="muted">Not issued</span>}
        </p>
        {b.policy_id ? <p className="doc-subno">Policy No.: {b.policy_id}</p> : null}

        <hr className="doc-hr" />

        <PartiesBlock b={b} company={COMPANY} />

        {planFacts.length > 0 && (
          <p className="doc-facts">
            {planFacts.map(([k, v], i) => (
              <span key={k}>{i ? ' · ' : ''}<b>{k}</b>: {v}</span>
            ))}
          </p>
        )}

        <table className="doc-table doc-gst">
          <colgroup>{[6, 12, 20, 38, 24].map((w, i) => <col key={i} style={{ width: `${w}%` }} />)}</colgroup>
          <thead>
            <tr>{['S No.', 'HSN/SAC', 'Policy No.', 'PAX Name', 'Amount (Incl. Taxes)'].map((h) => (
              <th key={h} className={h.startsWith('Amount') ? 'doc-num' : undefined}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {lines.map((t, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{INSURANCE_SAC}</td>
                {/* No policy number means the insurer never issued for this
                    traveller — say so rather than print a blank cell. */}
                <td>{t.policyId || 'Not issued'}</td>
                <td>{t.name || '—'}</td>
                <td className="doc-num">{n2(t.amount)}</td>
              </tr>
            ))}
            <tr>
              <td /><td /><td />
              <td><b>Total</b></td>
              <td className="doc-num"><b>{n2(total)}</b></td>
            </tr>
          </tbody>
        </table>

        <div className="doc-summary">
          {/* GST only when the insurer's split exists — printing 0.00 for tax we
              cannot see would be a false statement on a tax document. */}
          {hasFareSplit ? (
            <div>
              <p className="doc-h">GST Details :</p>
              <table className="doc-table doc-gst">
                <thead><tr><th>Description</th><th>CGST @9%</th><th>SGST @9%</th><th>IGST @18%</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Premium GST</td>
                    <td>{n2(cgst)}</td>
                    <td>{n2(sgst)}</td>
                    <td>{n2(igst)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : <div />}
          <div className={`doc-totals${hasFareSplit ? '' : ' doc-total-only'}`}>
            {hasFareSplit && (
              <>
                <div className="doc-trow"><span>Premium</span><span>{n2(premium)}</span></div>
                <div className="doc-trow"><span>GST</span><span>{n2(premiumGst)}</span></div>
                {/* Sub-rupee noise is rounding, not a fee. */}
                {processing >= 0.5 && (
                  <div className="doc-trow"><span>Processing fee</span><span>{n2(processing)}</span></div>
                )}
              </>
            )}
            <div className="doc-trow doc-net"><b>Total Amount</b><b>{rupee(total)}</b></div>
            {Number(b.refund_amount) > 0 ? (
              <p className="doc-paid-note">
                Refund of {rupee(b.refund_amount)} {b.refund_status ? `(${b.refund_status})` : ''} against this policy.
              </p>
            ) : null}
          </div>
        </div>

        <hr className="doc-hr" />

        <div className="doc-terms">
          <p className="doc-h">Terms &amp; Conditions :</p>
          <ul>
            <li>All cases &amp; disputes are subject to {COMPANY.jurisdiction} jurisdiction only.</li>
            <li>Insurance claims, coverage and exclusions are governed exclusively by the insurer’s policy wording.</li>
            <li>Refunds &amp; cancellations are subject to insurer approval.</li>
            {/* Tenant-configured terms (Tax Management), one <li> per line. */}
            {extraTerms(COMPANY.terms).map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>

        <p className="doc-foot">This is a Computer Generated Invoice, No Signature required.</p>
      </article>
    </>
  );
}
